import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { localeFor } from "@/lib/dateLocale";
import { RowsSkeleton } from "@/components/FeedSkeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from "@/components/ui/dialog";
import { MessageCircle, Search, Plus, Loader2, Check, CheckCheck, Trash2, Settings2, Pin, BellOff } from "lucide-react";
import { toast } from "sonner";
import { useIsMobile } from "@/lib/useIsMobile";
import { ConvActionsSheet } from "@/components/chat/ConvActionsSheet";
import { DmPrivacyDialog } from "@/components/chat/DmPrivacy";
import { CHAT_DRAFT_EVENT, dmDraftKey, getChatDraft, draftPreview, syncChatDrafts } from "@/lib/chatDrafts";
import { readCachedConversations, writeCachedConversations } from "@/lib/offlineCache";

const fmtTime = (iso, lang) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const loc = localeFor(lang);
    return d.toDateString() === now.toDateString()
      ? d.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString(loc, { day: "numeric", month: "short" });
  } catch { return ""; }
};

// Full-screen inbox — opens the real-time chat dock on selection.
export const Messages = () => {
  const { openChat, t, lang, user } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const viewProfile = (c) => { if (c?.other?.id) navigate(`/profile/${c.other.id}`); };
  // Lot 718 — démarrage instantané (garde-fou « instant_boot ») : la boîte de réception de la dernière visite s'affiche
  // tout de suite depuis l'appareil, puis la liste du serveur la remplace en silence (aussi en secours si le serveur échoue).
  const cachedConvos = () => (user?.id ? readCachedConversations(user.id)?.conversations || null : null);
  const [convos, setConvos] = useState(() => cachedConvos() || []);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(() => convos.length === 0);
  const [newOpen, setNewOpen] = useState(false);
  const [nq, setNq] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const [actionsConv, setActionsConv] = useState(null); // feuille d'actions (appui long / clic droit)
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const isMobile = useIsMobile();
  const pressTimer = useRef(null);
  const pressFired = useRef(false);
  const pressHandlers = (c) => ({
    onContextMenu: (e) => { e.preventDefault(); setActionsConv(c); },
    onPointerDown: (e) => { if (e.pointerType === "mouse") return; pressFired.current = false; clearTimeout(pressTimer.current); pressTimer.current = setTimeout(() => { pressFired.current = true; setActionsConv(c); }, 480); },
    onPointerUp: () => clearTimeout(pressTimer.current),
    onPointerMove: () => clearTimeout(pressTimer.current),
    onPointerCancel: () => clearTimeout(pressTimer.current),
  });
  const openConv = (c) => { if (pressFired.current) { pressFired.current = false; return; } openChat(c.other); };
  // Brouillons (lot 568) : la liste se met à jour quand on tape dans le dock à côté.
  const [, setDraftTick] = useState(0);
  useEffect(() => {
    const on = () => setDraftTick((n) => n + 1);
    window.addEventListener(CHAT_DRAFT_EVENT, on);
    syncChatDrafts(); // copie serveur (brouillons tapés sur un autre appareil)
    return () => window.removeEventListener(CHAT_DRAFT_EVENT, on);
  }, []);

  useEffect(() => {
    if (!newOpen) return;
    clearTimeout(searchTimer.current);
    const term = nq.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(() => {
      api.get(`/chat/search-users?q=${encodeURIComponent(term)}`)
        .then(({ data }) => setResults(data.users || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [nq, newOpen]);

  const startChat = (u) => { setNewOpen(false); setNq(""); setResults([]); openChat(u); };

  const load = () => api.get("/chat/conversations")
    .then(({ data }) => {
      const list = data.conversations || [];
      setConvos(list);
      if (user?.id) writeCachedConversations(user.id, list);
    })
    .catch(() => { const c = cachedConvos(); if (c) setConvos((cur) => (cur.length ? cur : c)); })
    .finally(() => setLoading(false));

  // Demande de message reçue : accepter (passe dans « Toutes ») ou supprimer (disparaît de ma boîte).
  const actOnRequest = async (c, action) => {
    try {
      await api.post(`/chat/conversations/${c.id}/request`, { action });
      toast.success(action === "accept" ? t("messages.request_accepted", "Demande acceptée") : t("messages.request_deleted", "Demande supprimée"));
      load();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
  };

  useEffect(() => {
    load();
    const on = () => load();
    window.addEventListener("yalola:chat", on);
    window.addEventListener("yalola:read", on);
    window.addEventListener("yalola:conv", on);
    return () => { window.removeEventListener("yalola:chat", on); window.removeEventListener("yalola:read", on); window.removeEventListener("yalola:conv", on); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: /messages?peer=<userId> auto-opens that conversation (from a
  // "Nouveau message" notification tap).
  useEffect(() => {
    const peer = searchParams.get("peer");
    if (!peer || convos.length === 0) return;
    const c = convos.find((x) => x.other?.id === peer);
    if (c?.other) {
      openChat(c.other);
      const sp = new URLSearchParams(searchParams);
      sp.delete("peer");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, convos]);

  const filtered = convos.filter((c) => {
    if (q.trim() && !(c.other?.name || "").toLowerCase().includes(q.toLowerCase())) return false;
    if (tab === "requests") return !!c.is_request;
    if (c.is_request) return false;
    if (tab === "archived") return !!c.archived;
    if (c.archived) return false;
    if (tab === "unread" && !((c.unread || 0) > 0 || c.manual_unread)) return false;
    return true;
  }).sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  const unreadTotal = convos.reduce((n, c) => n + (!c.is_request && !c.archived && ((c.unread || 0) > 0 || c.manual_unread) ? 1 : 0), 0);
  const requestsTotal = convos.reduce((n, c) => n + (c.is_request ? 1 : 0), 0);
  const archivedTotal = convos.reduce((n, c) => n + (c.archived && !c.is_request ? 1 : 0), 0);
  const tabs = [
    { id: "all", label: t("messages.all", "Toutes") },
    { id: "unread", label: t("messages.unread", "Non lues"), count: unreadTotal },
    { id: "requests", label: t("messages.requests", "Demandes"), count: requestsTotal },
    ...(archivedTotal > 0 || tab === "archived" ? [{ id: "archived", label: t("messages.archived", "Archivées"), count: archivedTotal }] : []),
  ];
  const emptyText = tab === "requests" ? t("messages.empty_requests", "Aucune demande de message.")
    : tab === "archived" ? t("messages.empty_archived", "Aucune conversation archivée.")
    : tab === "unread" ? t("messages.empty_unread", "Aucune conversation non lue.")
    : t("messages.empty", "Aucune conversation pour le moment. Ouvrez le profil d'un membre pour discuter.");

  return (
    <AppShell>
      <div className="yalola-card p-0 overflow-hidden" data-testid="messages-page">
        <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--yalola-border)" }}>
          <MessageCircle size={20} style={{ color: "var(--yalola-primary)" }} />
          <h1 className="text-lg font-bold flex-1">{t("nav.messages", "Messagerie")}</h1>
          <button onClick={() => setPrivacyOpen(true)} className="h-9 w-9 rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text-muted)" }} data-testid="messages-privacy-btn" aria-label={t("messages.privacy_btn", "Qui peut m'écrire")} title={t("messages.privacy_btn", "Qui peut m'écrire")}>
            <Settings2 size={18} />
          </button>
          <button onClick={() => setNewOpen(true)} className="h-9 w-9 rounded-full grid place-items-center text-white shrink-0 hover:opacity-90" style={{ background: "var(--yalola-primary)" }} data-testid="new-message-btn" aria-label={t("messages.new", "Nouveau message")}>
            <Plus size={18} />
          </button>
        </div>
        <div className="px-3 py-2 border-b flex items-center gap-2 overflow-x-auto" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-bg)" }} data-testid="messages-filter-tabs">
          {tabs.map((f) => {
            const active = tab === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setTab(f.id)}
                className="px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5"
                style={active
                  ? { background: "var(--yalola-primary)", color: "#fff" }
                  : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}
                data-testid={`messages-filter-${f.id}`}
              >
                {f.label}
                {f.count > 0 && (
                  <span className="min-w-[16px] h-4 px-1 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: active ? "rgba(255,255,255,0.25)" : "var(--yalola-primary)", color: "#fff" }} data-testid={`messages-filter-${f.id}-count`}>{f.count}</span>
                )}
              </button>
            );
          })}
        </div>
        {tab === "requests" && (
          <div className="px-4 py-2 text-xs border-b" style={{ color: "var(--yalola-text-muted)", borderColor: "var(--yalola-border)" }} data-testid="messages-requests-hint">
            {t("messages.requests_hint", "Messages de membres qui ne te suivent pas. Accepte pour discuter, ou supprime : ils ne sauront pas que tu as vu leur message.")}
          </div>
        )}
        <div className="p-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("messages.search", "Rechercher une conversation")}
              className="w-full rounded-full border pl-9 pr-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}
              data-testid="messages-search"
            />
          </div>
        </div>
        <div>
          {loading && <RowsSkeleton count={7} />}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center" data-testid="messages-empty">
              <MessageCircle size={40} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{emptyText}</p>
            </div>
          )}
          {filtered.map((c) => {
            const unread = (c.unread || 0) > 0 || !!c.manual_unread;
            const mineLast = c.last_message_sender && user && c.last_message_sender === user.id;
            const lastRead = (c.last_read || {})[c.other?.id];
            const seen = mineLast && lastRead && c.last_message_at && lastRead >= c.last_message_at;
            const draft = draftPreview(getChatDraft(dmDraftKey(c.other?.id)));
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => openConv(c)}
                onKeyDown={(e) => { if (e.key === "Enter") openChat(c.other); }}
                {...pressHandlers(c)}
                className="w-full flex items-center gap-3 px-4 py-3 border-b hover:bg-[color:var(--yalola-surface-alt)] transition-colors text-left cursor-pointer select-none"
                style={{ borderColor: "var(--yalola-border)", background: unread ? "var(--yalola-primary-soft)" : undefined, WebkitTouchCallout: "none" }}
                data-testid={`conversation-${c.other?.id}`}
                data-request={c.is_request ? "1" : undefined}
                data-pinned={c.pinned ? "1" : undefined}
                data-muted={c.muted ? "1" : undefined}
                data-archived={c.archived ? "1" : undefined}
              >
                <div className="relative shrink-0" onClick={c.is_request ? (e) => { e.stopPropagation(); viewProfile(c); } : undefined} data-testid={c.is_request ? `request-avatar-${c.other?.id}` : undefined}>
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={avatarSrc(c.other)} />
                    <AvatarFallback>{c.other?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  {unread && <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white" style={{ background: "var(--yalola-primary)" }} />}
                  {c.other?.online && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white" style={{ background: "#22c55e" }} data-testid={`online-dot-${c.other?.id}`} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`truncate flex items-center gap-1 ${unread ? "font-bold" : "font-semibold"}`}>{c.other?.name || "—"} <VerifiedBadge user={c.other} size={13} /></span>
                    <span className="text-xs shrink-0 flex items-center gap-1" style={{ color: unread ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}>
                      {c.muted && <BellOff size={12} data-testid={`conversation-muted-${c.other?.id}`} />}
                      {c.pinned && <Pin size={12} data-testid={`conversation-pinned-${c.other?.id}`} />}
                      {fmtTime(c.last_message_at, lang)}
                    </span>
                  </div>
                  <div className="text-sm truncate flex items-center gap-1" style={{ color: unread ? "var(--yalola-text)" : "var(--yalola-text-muted)", fontWeight: unread ? 600 : 400 }}>
                    {draft ? (
                      <span className="truncate" data-testid={`conversation-draft-${c.other?.id}`}>
                        <span className="font-semibold" style={{ color: "var(--yalola-primary)" }}>{t("chat.draft_label", "Brouillon :")}</span> {draft}
                      </span>
                    ) : (
                      <>
                        {mineLast && (
                          seen
                            ? <CheckCheck size={15} className="shrink-0" style={{ color: "var(--yalola-primary)" }} data-testid={`conversation-receipt-read-${c.other?.id}`} />
                            : <Check size={15} className="shrink-0" style={{ color: "var(--yalola-text-muted)" }} data-testid={`conversation-receipt-sent-${c.other?.id}`} />
                        )}
                        <span className="truncate">{c.last_message || "…"}</span>
                      </>
                    )}
                  </div>
                  {c.is_request && (
                    <button onClick={(e) => { e.stopPropagation(); viewProfile(c); }} className="text-xs font-semibold underline underline-offset-2 mt-0.5" style={{ color: "var(--yalola-primary)" }} data-testid={`request-profile-${c.other?.id}`}>{t("matching.view_profile", "Voir le profil")}</button>
                  )}
                </div>
                {c.is_request ? (
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => actOnRequest(c, "accept")} className="h-8 px-3 rounded-full text-xs font-semibold text-white" style={{ background: "var(--yalola-primary)" }} data-testid={`request-accept-${c.other?.id}`}>{t("messages.request_accept", "Accepter")}</button>
                    <button onClick={() => actOnRequest(c, "delete")} className="h-8 w-8 grid place-items-center rounded-full text-red-600" style={{ background: "var(--yalola-surface-alt)" }} aria-label={t("messages.request_delete", "Supprimer")} data-testid={`request-delete-${c.other?.id}`}><Trash2 size={15} /></button>
                  </div>
                ) : unread && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-bold text-white shrink-0"
                    style={{ background: "var(--yalola-primary)" }}
                    data-testid={`conversation-unread-${c.other?.id}`}
                  >
                    {(c.unread || 0) > 9 ? "9+" : (c.unread || 0) > 0 ? c.unread : "•"}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ConvActionsSheet conv={actionsConv} t={t} isMobile={isMobile} onClose={() => setActionsConv(null)} onChanged={load} onDeleted={load} onViewProfile={viewProfile} />
      <DmPrivacyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} />

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent variant="sheet" className="sm:max-w-md" data-testid="new-message-dialog">
          <DialogHeader><DialogTitle>{t("messages.new", "Nouveau message")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{t("messages.search_member", "Rechercher un membre…")}</DialogDescription>
          <DialogBody className="space-y-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <input
              autoFocus
              value={nq}
              onChange={(e) => setNq(e.target.value)}
              placeholder={t("messages.search_member", "Rechercher un membre…")}
              className="w-full rounded-full border pl-9 pr-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}
              data-testid="new-message-search"
            />
          </div>
          <div className="-mx-1">
            {searching && <div className="py-6 text-center"><Loader2 className="animate-spin mx-auto" style={{ color: "var(--yalola-primary)" }} /></div>}
            {!searching && nq.trim().length >= 2 && results.length === 0 && (
              <p className="py-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="new-message-empty">{t("messages.no_results", "Aucun membre trouvé")}</p>
            )}
            {results.map((u) => (
              <button key={u.id} onClick={() => startChat(u)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[color:var(--yalola-surface-alt)] text-left" data-testid={`new-message-result-${u.id}`}>
                <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1">{u.name} <VerifiedBadge user={u} size={13} /></div>
                  {u.city && <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{u.city}</div>}
                </div>
              </button>
            ))}
          </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default Messages;
